import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react'
import { useBackendLinksProvider } from './BackendLinksProvider'
import { Socket, io } from 'socket.io-client'
import ConfirmationModal from '../Components/ConfirmationModal/ConfirmationModal'
import { MobergTheme } from '../Moberg'
import { useModalProvider } from './ModalProvider'
import { useAuthProvider } from './AuthProvider'
import { useWorkspacesProvider } from './WorkspacesProvider'

type SocketConnectionOptions = {
    namespace: string
}

type SocketContextValue = {
    getDataQuerySocket: (id?: string, options?: SocketConnectionOptions) => Socket
}

const SocketContext = createContext<SocketContextValue>({} as SocketContextValue)

const getSocketKey = (id: string, options?: SocketConnectionOptions) => {
    return `${id}-${options?.namespace.replace("/", "") ?? "default"}`
}

export const SocketProvider: React.FC = ({ children }) => {
    const { LINKS } = useBackendLinksProvider()
    const sockets = useRef(new Map<string, Socket>())
    const { createModal } = useModalProvider()
    const { token, userIsLoggedIn } = useAuthProvider()
    const { selectedWorkspace } = useWorkspacesProvider()

    const value = useMemo(() => {
        const getDataQuerySocket = (id = "default", options?: SocketConnectionOptions): Socket => {
            const key = getSocketKey(id, options)
            let socket = sockets.current.get(key)

            if (!socket) {
                let url = LINKS.QUERY

                if (options?.namespace) {
                    url += options.namespace
                }

                socket = io(url, { 
                    transports: ["websocket"], 
                    query: { id: key },
                    reconnection: true,
                    auth: {
                        token: token ?? "",
                        workspace_name: selectedWorkspace ?? ""
                    }
                })

                sockets.current.set(key, socket)

                socket.on("connect_error", error => {
                    console.error(error)
                    // Socket.IO v4 does NOT auto-reconnect after a namespace-level
                    // rejection (server-sent CONNECT_ERROR or middleware reject).
                    // The Socket is destroyed (`active: false`, `subs` cleared) but
                    // its `connected` flag remains stale (true). Plain `connect()`
                    // would short-circuit on `if (this.connected) return this`,
                    // so we call `disconnect()` first to reset the flag (and clean
                    // up internal state), then `connect()` to re-run `subEvents()`
                    // and re-issue the namespace handshake.
                    setTimeout(() => {
                        if (socket) {
                            socket.disconnect()

                            if (token) {
                                socket.connect()
                            } else {
                                // Allow this socket entry to be recreated on the next attempt.
                                sockets.current.delete(key)
                            }
                        }
                    }, 1000)
                })
                
                // These are internal errors passed back by the server, intended to alert the user of an issue.
                socket.on("error", ({ error }) => {
                    console.error(error)
                    createModal(
                        <ConfirmationModal
                            title={"Server Problem"}
                            description={`Please contact the system administrator and provide them this message: "${error}"`}
                            confirmButton={{ text: "OK", theme: MobergTheme.BLUE }}
                            showCancelButton={false} />
                    )
                })
            }

            // This logic is very important for re-establishing a socket connection. 
            // SocketIO does not automatically reconnect, even with "reconnection: true" or "autoConnect: true".
            if (socket.disconnected) {
                socket.connect()
            } 

            return socket
        }

        // Return the functions so they are accessible in the context
        return { getDataQuerySocket }
    }, [LINKS.QUERY, createModal, selectedWorkspace, token])

    // If the user is not logged in, make sure all of the socket connections get closed.
    useEffect(() => {
        if (!userIsLoggedIn) {
            sockets.current.forEach(socket => socket.close())
            sockets.current = new Map()
        }
    })

    return <SocketContext.Provider value={value}>
        {children}
    </SocketContext.Provider>
}

export const useSocketProvider = () => useContext(SocketContext)
